/**
 * Static annex download client — Documents-tab "Export new" for the firm's
 * standing annexes that are served as-is (PT/EN .docx), not data-populated:
 *   - kind="pp"   → Annex III — Policies & Procedures
 *   - kind="fees" → Annex I — Fees (static for now; #17 will populate it)
 *
 * Same raw same-origin fetch + Content-Disposition filename pattern as the
 * IAA / AQ exports; reuses downloadBlob.
 */
import { downloadBlob } from './iaaExports';
import type { DocumentLanguage, UUID } from './types';

export { downloadBlob };

export type AnnexKind = 'pp' | 'fees' | 'samplemonthly';

async function detailOf(res: Response, fallback: string): Promise<string> {
  let detail: unknown = null;
  try { detail = (await res.json()).detail; } catch { /* non-JSON */ }
  return typeof detail === 'string' ? detail : fallback;
}

/** Output format for a data-populated annex. Annex I (fees) supports both (Legal-docs
 * PDF arc Inc 1); other kinds ignore it and serve their native type. */
export type AnnexFormat = 'word' | 'pdf';

/** One fee schedule's simulation for the agreement's Annex I PDF (Pedro 2026-10-10/11): a
 *  schedule assigned to the agreement's accounts and an amount. The billing frequencies and
 *  VAT are those accounts' own (the server reads them). */
export interface AnnexFeeExample {
  scheduleId: UUID;
  amountEur: number;
  /** The billing frequency chosen next to the amount (Pedro's smoke 2026-10-11); the example
   *  shows only it. Omitted = every frequency the schedule's accounts use. */
  frequency?: 'monthly' | 'quarterly' | 'semiannual';
}

export async function downloadAnnex(
  agreementId: UUID,
  kind: AnnexKind,
  language: DocumentLanguage,
  format: AnnexFormat = 'word',
  examples?: AnnexFeeExample[],
): Promise<{ blob: Blob; fileName: string }> {
  // One `example=<schedule>:<amount>[:<frequency>]` per schedule on the agreement's accounts.
  const sim = (examples ?? [])
    .map((e) => `&example=${e.scheduleId}:${e.amountEur}${e.frequency ? `:${e.frequency}` : ''}`)
    .join('');
  const res = await fetch(
    `/agreements/${agreementId}/annex/${kind}?language=${language}&format=${format}${sim}`,
    { method: 'GET', credentials: 'include' },
  );
  if (!res.ok) {
    throw new Error(await detailOf(res, 'The annex could not be generated.'));
  }
  const cd = res.headers.get('Content-Disposition') ?? '';
  const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
  const fileName = m ? decodeURIComponent(m[1]) : `${kind}.${format === 'pdf' ? 'pdf' : 'docx'}`;
  return { blob: await res.blob(), fileName };
}
